import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  CalendarCheck,
  CheckCircle2,
  Clock,
  ExternalLink,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Users,
  Video,
} from "lucide-react";
import { useInterviews, useCandidates, useNeeds } from "@/hooks/useStaffing";
import InterviewsWorkflowModal from "@/components/admin/recruitment/InterviewsWorkflowModal";
import ConfirmationModal from "@/components/ui/ConfirmationModal";

const STATUS_COLORS = {
  scheduled: "bg-blue-500/10 text-blue-300 border-blue-500/20",
  completed: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  cancelled: "bg-rose-500/10 text-rose-300 border-rose-500/20",
  declined: "bg-amber-500/10 text-amber-300 border-amber-500/20",
};

const STATUS_LABELS = {
  scheduled: "Planifié",
  completed: "Réalisé",
  cancelled: "Annulé",
  declined: "Décliné",
};

export default function InterviewsListPage() {
  const { interviews, loading, error, fetchInterviews, deleteInterview } = useInterviews();
  const { candidates } = useCandidates();
  const { needs } = useNeeds();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeModalInterview, setActiveModalInterview] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [interviewToDelete, setInterviewToDelete] = useState(null);
  const [deletingInterview, setDeletingInterview] = useState(false);

  // New interview modal candidate selector
  const [selectedCandidateId, setSelectedCandidateId] = useState("");

  const candidateMap = useMemo(() => {
    const map = {};
    candidates.forEach((c) => {
      map[c._id] = `${c.first_name} ${c.last_name}`;
    });
    return map;
  }, [candidates]);

  const needMap = useMemo(() => {
    const map = {};
    needs.forEach((n) => {
      map[n._id] = n.title;
    });
    return map;
  }, [needs]);

  const filteredInterviews = useMemo(() => {
    return interviews.filter((iv) => {
      if (statusFilter !== "all" && iv.status !== statusFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const candName = (candidateMap[iv.candidate_id] || "").toLowerCase();
        const needTitle = (needMap[iv.need_id] || "").toLowerCase();
        const formatMatch = (iv.format || "").toLowerCase().includes(q);
        const feedbackMatch = (iv.feedback || "").toLowerCase().includes(q);
        return candName.includes(q) || needTitle.includes(q) || formatMatch || feedbackMatch;
      }
      return true;
    });
  }, [interviews, statusFilter, search, candidateMap, needMap]);

  return (
    <div className="space-y-6 pb-16" data-testid="interviews-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-indigo-500/10 px-3 py-0.5 text-xs font-semibold text-indigo-300 border border-indigo-500/20 mb-2">
            <CalendarCheck className="h-3.5 w-3.5" />
            Parcours Entretiens L7
          </div>
          <h1 className="text-2xl font-heading font-extrabold text-white tracking-tight">
            Entretiens de Recrutement
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Planification Teams, créneaux préférés, gestion des refus et comptes rendus qualitatifs.
          </p>
        </div>

        <button
          onClick={() => setShowNewModal(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-xs font-semibold text-white shadow-md hover:bg-indigo-500 transition-all self-start sm:self-auto"
          data-testid="create-interview-button"
        >
          <Plus className="h-4 w-4" />
          Planifier un entretien (L7)
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par candidat, mission, format..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/80 border border-white/10 text-xs text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
            data-testid="interviews-search-input"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
          {[
            { id: "all", label: "Tous" },
            { id: "scheduled", label: "Planifiés" },
            { id: "completed", label: "Réalisés" },
            { id: "declined", label: "Déclinés" },
            { id: "cancelled", label: "Annulés" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                statusFilter === tab.id
                  ? "bg-indigo-600 text-white font-semibold"
                  : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 text-rose-300 text-xs">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredInterviews.length === 0 && (
        <div className="text-center py-16 glass rounded-2xl border border-white/10 text-slate-500">
          <CalendarCheck className="h-10 w-10 mx-auto text-slate-600 mb-3" />
          <p className="text-base font-semibold text-slate-300">Aucun entretien enregistré</p>
          <p className="text-xs text-slate-500 mt-1">
            {search ? "Ajustez vos filtres de recherche." : "Planifiez un entretien lié à une candidature."}
          </p>
        </div>
      )}

      {/* Interviews List Cards */}
      {!loading && filteredInterviews.length > 0 && (
        <div className="grid gap-3.5">
          {filteredInterviews.map((iv) => {
            const candName = candidateMap[iv.candidate_id] || "Candidat";
            const needTitle = needMap[iv.need_id] || "Mission LENVIA";

            return (
              <div
                key={iv._id}
                className="glass rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                data-testid={`interview-card-${iv._id}`}
              >
                <div className="space-y-1.5 min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="font-bold text-white text-base">
                      {candName}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full border ${
                        STATUS_COLORS[iv.status || "scheduled"] || STATUS_COLORS.scheduled
                      }`}
                    >
                      {STATUS_LABELS[iv.status || "scheduled"] || iv.status}
                    </span>
                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-indigo-300 border border-white/10">
                      {needTitle}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1 text-slate-300">
                      <Clock className="h-3.5 w-3.5 text-slate-500" />
                      {iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString("fr-FR") : "Date non fixée"}
                    </span>
                    {iv.timezone && (
                      <span className="text-slate-500">
                        Fuseau : <span className="text-slate-300">{iv.timezone}</span>
                      </span>
                    )}
                    {iv.interview_type && (
                      <span className="text-slate-500">
                        Type : <span className="text-slate-300">{iv.interview_type}</span>
                      </span>
                    )}
                    {iv.teams_meeting_url && (
                      <a
                        href={iv.teams_meeting_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 font-semibold"
                      >
                        <Video className="h-3.5 w-3.5" />
                        Lien Teams
                      </a>
                    )}
                  </div>

                  {/* Feedback preview */}
                  {iv.feedback && (
                    <div className="p-2.5 rounded-xl bg-slate-900 border border-white/5 text-xs text-slate-300 max-w-3xl">
                      <span className="text-slate-500 font-semibold">Compte rendu : </span>
                      {iv.feedback}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                  <button
                    type="button"
                    onClick={() => setActiveModalInterview(iv)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30 rounded-xl text-xs font-semibold transition-colors"
                    data-testid={`manage-interview-btn-${iv._id}`}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    Gérer & Évaluer
                  </button>

                  <button
                    type="button"
                    onClick={() => setInterviewToDelete(iv)}
                    className="p-1.5 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit / Manage Interview Modal */}
      {activeModalInterview && (
        <InterviewsWorkflowModal
          isOpen={Boolean(activeModalInterview)}
          onClose={() => {
            setActiveModalInterview(null);
            void fetchInterviews();
          }}
          interviewId={activeModalInterview._id}
          candidateId={activeModalInterview.candidate_id}
          applicationId={activeModalInterview.candidate_need_id}
          candidateName={candidateMap[activeModalInterview.candidate_id] || "Candidat"}
          missionTitle={needMap[activeModalInterview.need_id] || "Mission LENVIA"}
        />
      )}

      {/* New Interview Dialog (Candidate Selection) */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101426] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-indigo-400" />
              Sélectionner le candidat pour l&apos;entretien
            </h2>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Candidat *</label>
                <select
                  value={selectedCandidateId}
                  onChange={(e) => setSelectedCandidateId(e.target.value)}
                  className="w-full rounded-lg bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-indigo-500 focus:outline-none"
                >
                  <option value="">-- Choisir un candidat --</option>
                  {candidates.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.first_name} {c.last_name} ({c.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={!selectedCandidateId}
                  onClick={() => {
                    const c = candidates.find((cand) => cand._id === selectedCandidateId);
                    setActiveModalInterview({
                      _id: undefined,
                      candidate_id: selectedCandidateId,
                      candidate_need_id: c?.pipeline?.[0]?._id,
                      need_id: c?.pipeline?.[0]?.need_id,
                    });
                    setShowNewModal(false);
                  }}
                  className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-xs font-semibold text-white shadow-md"
                >
                  Continuer vers planification
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Confirmation Modal for deletion */}
      <ConfirmationModal
        isOpen={Boolean(interviewToDelete)}
        onClose={() => setInterviewToDelete(null)}
        title="Supprimer l'entretien"
        message="Voulez-vous vraiment supprimer cet entretien du planning ?"
        confirmText="Supprimer l'entretien"
        variant="danger"
        isSubmitting={deletingInterview}
        onConfirm={async () => {
          if (!interviewToDelete) return;
          setDeletingInterview(true);
          try {
            await deleteInterview(interviewToDelete._id);
            setInterviewToDelete(null);
          } finally {
            setDeletingInterview(false);
          }
        }}
        testId="delete-interview-modal"
      />
    </div>
  );
}
